/**
 * DeclaracoesEpiSetor
 * A tabela de um setor no painel de declarações de EPI: uma linha por cargo, uma coluna por
 * declaração, e uma primeira linha "Todos os cargos do setor" que aplica a mesma resposta à
 * coluna inteira. O setor abre e fecha — com muitos setores, a tela começa fechada.
 */

import React from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, Chip, NativeSelect, Typography, alpha } from '@mui/material';
import { ExpandMore as ExpandirIcon } from '@mui/icons-material';
import { DataTable, type DataTableColumn } from '../../../components/common';
import type {
  LtcatEpiDeclaracaoChave,
  LtcatEpiDeclaracoesPainel,
  LtcatEpiResposta,
} from '../../../types/document';
import {
  CHAVES_DAS_DECLARACOES,
  ROTULO_DA_RESPOSTA,
  ROTULO_DE_PENDENTE,
  chaveDaCelula,
  respostaVigente,
  type MapaDeRespostas,
  type RespostaDaCelula,
} from './declaracoesEpi';

type Setor = LtcatEpiDeclaracoesPainel['setores'][number];
type Cargo = Setor['cargos'][number];

type LinhaDoSetor = { tipo: 'todos' } | { tipo: 'cargo'; cargo: Cargo };

const VALOR_MISTO = '__misto';
const RESPOSTAS: readonly LtcatEpiResposta[] = ['S', 'N', 'NA'];

const respostaDoValor = (valor: string): RespostaDaCelula =>
  RESPOSTAS.find((resposta) => resposta === valor) ?? null;

interface SeletorDeRespostaProps {
  nome: string;
  valor: string;
  desabilitado: boolean;
  alterada?: boolean;
  misto?: boolean;
  onEscolher: (resposta: RespostaDaCelula) => void;
}

const SeletorDeResposta: React.FC<SeletorDeRespostaProps> = ({
  nome,
  valor,
  desabilitado,
  alterada = false,
  misto = false,
  onEscolher,
}) => (
  <NativeSelect
    value={valor}
    disabled={desabilitado}
    onChange={(event) => onEscolher(respostaDoValor(event.target.value))}
    inputProps={{ 'aria-label': alterada ? `${nome} (alterada, não salva)` : nome }}
    sx={{
      minWidth: 132,
      minHeight: 44,
      ...(alterada && {
        bgcolor: (tema) => alpha(tema.palette.warning.main, 0.16),
        fontWeight: 600,
      }),
    }}
  >
    {misto && (
      <option value={VALOR_MISTO} disabled>
        Cargos diferentes
      </option>
    )}
    <option value="">{ROTULO_DE_PENDENTE}</option>
    {RESPOSTAS.map((resposta) => (
      <option key={resposta} value={resposta}>
        {ROTULO_DA_RESPOSTA[resposta]}
      </option>
    ))}
  </NativeSelect>
);

export interface DeclaracoesEpiSetorProps {
  setor: Setor;
  /** Os cargos a mostrar (a busca pode ter escondido parte deles). */
  cargosVisiveis: Cargo[];
  carregadas: MapaDeRespostas;
  rascunho: MapaDeRespostas;
  somenteLeitura: boolean;
  aberto: boolean;
  onAlternar: () => void;
  onEscolher: (occupationId: number | string, chave: LtcatEpiDeclaracaoChave, resposta: RespostaDaCelula) => void;
  /** Aplica a resposta a todos os cargos do setor, não só aos que a busca deixou à vista. */
  onAplicarATodos: (setor: Setor, chave: LtcatEpiDeclaracaoChave, resposta: RespostaDaCelula) => void;
}

export const DeclaracoesEpiSetor: React.FC<DeclaracoesEpiSetorProps> = ({
  setor,
  cargosVisiveis,
  carregadas,
  rascunho,
  somenteLeitura,
  aberto,
  onAlternar,
  onEscolher,
  onAplicarATodos,
}) => {
  const vigente = (cargo: Cargo, chave: LtcatEpiDeclaracaoChave): RespostaDaCelula =>
    respostaVigente(carregadas, rascunho, chaveDaCelula(cargo.occupationId, chave));

  const semResposta = setor.cargos.reduce(
    (total, cargo) => total + CHAVES_DAS_DECLARACOES.filter((chave) => vigente(cargo, chave) === null).length,
    0
  );

  const colunas: DataTableColumn<LinhaDoSetor>[] = [
    {
      id: 'cargo',
      label: 'Cargo',
      primary: true,
      render: (linha) =>
        linha.tipo === 'todos' ? (
          <Typography variant="body2" fontWeight={600}>
            Todos os cargos do setor
          </Typography>
        ) : (
          linha.cargo.occupationName
        ),
    },
    ...CHAVES_DAS_DECLARACOES.map((chave, indice): DataTableColumn<LinhaDoSetor> => {
      const nome = `Declaração ${indice + 1}`;
      return {
        id: chave,
        label: nome,
        render: (linha) => {
          if (linha.tipo === 'cargo') {
            const celula = chaveDaCelula(linha.cargo.occupationId, chave);
            return (
              <SeletorDeResposta
                nome={`${nome} — ${linha.cargo.occupationName}`}
                valor={vigente(linha.cargo, chave) ?? ''}
                desabilitado={somenteLeitura}
                alterada={celula in rascunho}
                onEscolher={(resposta) => onEscolher(linha.cargo.occupationId, chave, resposta)}
              />
            );
          }
          const valores = new Set(setor.cargos.map((cargo) => vigente(cargo, chave) ?? ''));
          const unico = valores.size === 1 ? [...valores][0] : null;
          return (
            <SeletorDeResposta
              nome={`${nome} — aplicar a todos os cargos de ${setor.departmentName}`}
              valor={unico ?? VALOR_MISTO}
              misto={unico === null}
              desabilitado={somenteLeitura}
              onEscolher={(resposta) => onAplicarATodos(setor, chave, resposta)}
            />
          );
        },
      };
    }),
  ];

  const linhas: LinhaDoSetor[] = [
    ...(setor.cargos.length > 1 ? [{ tipo: 'todos' } as const] : []),
    ...cargosVisiveis.map((cargo) => ({ tipo: 'cargo', cargo }) as const),
  ];

  return (
    <Accordion expanded={aberto} onChange={onAlternar} disableGutters variant="outlined" sx={{ mb: 1.5 }}>
      <AccordionSummary expandIcon={<ExpandirIcon />} sx={{ minHeight: 56 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1">{setor.departmentName}</Typography>
          <Typography variant="body2" color="text.secondary">
            {setor.cargos.length === 1 ? '1 cargo' : `${setor.cargos.length} cargos`}
          </Typography>
          {semResposta > 0 && (
            <Chip size="small" variant="outlined" label={`${semResposta} sem resposta`} />
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ px: { xs: 0, sm: 2 } }}>
        <DataTable
          columns={colunas}
          rows={linhas}
          getRowId={(linha) => (linha.tipo === 'todos' ? `todos-${setor.departmentId}` : String(linha.cargo.occupationId))}
          emptyState={{
            title: 'Nenhum cargo com este nome neste setor',
            description: 'Mude ou apague a busca para ver os cargos do setor.',
          }}
        />
      </AccordionDetails>
    </Accordion>
  );
};

export default DeclaracoesEpiSetor;
